import React, { useState, useEffect } from 'react';
import { Typography, IconButton, Menu, MenuItem } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { fetchPurchaseSummary } from '../services/api';
import affiliateIllustration from '../assets/affiliate-illustration.svg';

// Format number with locale
const formatNumber = (number) => {
  return new Intl.NumberFormat('id-ID').format(number);
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('id-ID').format(amount);
};

const Referral = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    totalVouchers: 0,
    totalUsers: 0,
    totalReward: 0
  });
  const [vouchers, setVouchers] = useState([]);
  const [filteredVouchers, setFilteredVouchers] = useState([]);
  const [searchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({
    key: 'code',
    direction: 'ascending'
  });
  const [sortAnchorEl, setSortAnchorEl] = useState(null);

  // Calculate pagination values
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  
  // Fetch purchase summary data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const summaryData = await fetchPurchaseSummary();
        setSummary(summaryData);
        // Transform voucher data from summary
        if (summaryData.vouchers && Array.isArray(summaryData.vouchers)) {
          setVouchers(summaryData.vouchers);
          setFilteredVouchers(summaryData.vouchers);
        }
      } catch (error) {
        console.error('Error fetching referral data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Apply search filter
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredVouchers(vouchers);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = vouchers.filter(voucher => 
        voucher.code.toLowerCase().includes(term)
      );
      setFilteredVouchers(filtered);
    }
    setPage(1); // Reset to first page when searching
  }, [searchTerm, vouchers]);

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
    
    // Apply sort
    const sortedVouchers = [...filteredVouchers].sort((a, b) => {
      if (direction === 'ascending') {
        return a[key] > b[key] ? 1 : -1;
      } else {
        return a[key] < b[key] ? 1 : -1;
      }
    });
    
    setFilteredVouchers(sortedVouchers);
    handleSortClose();
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  // Pagination handlers
  const handlePreviousPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleNextPage = () => {
    setPage((prevPage) => Math.min(prevPage + 1, Math.ceil(filteredVouchers.length / rowsPerPage)));
  };

  return (
    <div className="referral-page">

      {/* Illustration */}
      <div className="illustration">
        <img 
          src={affiliateIllustration} 
          alt="Affiliate Illustration" 
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/200x150?text=Affiliate+Illustration';
          }}
        />
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-card-title">TOTAL REWARD</div>
          <div className="summary-card-value positive">
            Rp {formatCurrency(summary.total_amount)}
            <ArrowDropUpIcon style={{ fontSize: 16, verticalAlign: 'middle' }} />
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-card-title">TOTAL VOUCHER</div>
          <div className="summary-card-value">{summary.total_voucher}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-title">TOTAL USER</div>
          <div className="summary-card-value">{formatNumber(summary.total_user)}</div>
        </div>
      </div>
      
      {/* Voucher Table */}
      <div className="voucher-table">
        <div className="table-header">
          <Typography variant="h6" className="table-title">Vouchers</Typography>
          <IconButton onClick={handleSortClick} size="small" className="sort-button">
            <SortIcon />
          </IconButton>
          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={handleSortClose}
          >
            <MenuItem onClick={() => handleSort('code')}>Sort by Name</MenuItem>
            <MenuItem onClick={() => handleSort('redemptions')}>Sort by Users</MenuItem>
            <MenuItem onClick={() => handleSort('reward')}>Sort by Reward</MenuItem>
          </Menu>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>
                  Voucher Name
                  {sortConfig.key === 'code' && (
                    sortConfig.direction === 'ascending' ? 
                      <ArrowDropUpIcon fontSize="small" /> : 
                      <ArrowDropDownIcon fontSize="small" />
                  )}
                </th>
                <th>
                  Total User
                  {sortConfig.key === 'redemptions' && (
                    sortConfig.direction === 'ascending' ? 
                      <ArrowDropUpIcon fontSize="small" /> : 
                      <ArrowDropDownIcon fontSize="small" />
                  )}
                </th>
                <th>
                  Reward
                  {sortConfig.key === 'reward' && (
                    sortConfig.direction === 'ascending' ? 
                      <ArrowDropUpIcon fontSize="small" /> : 
                      <ArrowDropDownIcon fontSize="small" />
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center' }}>Loading...</td>
                </tr>
              ) : filteredVouchers.length === 0 ? (
                <tr>
                  <td colSpan="3" style={{ textAlign: 'center' }}>No vouchers found</td>
                </tr>
              ) : (
                filteredVouchers.map((voucher) => (
                  <tr key={voucher.code}>
                    <td>{voucher.code}</td>
                    <td>{formatNumber(voucher.total_user)}</td>
                    <td>Rp {formatCurrency(voucher.total_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination-text">
            {filteredVouchers.length > 0 ? 
              `${startIndex + 1}-${Math.min(endIndex, filteredVouchers.length)} of ${filteredVouchers.length}` : 
              '0 items'}
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-button" 
              onClick={handlePreviousPage} 
              disabled={page === 1}
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </button>
            <button 
              className="pagination-button" 
              onClick={handleNextPage} 
              disabled={page >= Math.ceil(filteredVouchers.length / rowsPerPage)}
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Referral;